import axios from 'axios';
import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import DatadogApi from '@/common/apis/DatadogApi';
import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import EntityProperty from '@/common/models/EntityProperty';
import Exception from '@/common/models/Exception';
import Location, { ILocationAPI } from '@/common/models/geo/Location';
import { FilterCollection, FilterComparisonOperator, QueryParams } from '@/common/models/query-params';
import Unit, { IUnitAPI } from '@/common/models/unit/Unit';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { DASH, getStore, GoogleMapsApiKey, isString } from '@/globals';
import orgAppStore from '@/org-app/store';
import rootStore from '@/store';
import {
  UPDATE_SITE_DESCRIPTION,
  UPDATE_SITE_EXTERNALID,
  UPDATE_SITE_LATITUDE,
  UPDATE_SITE_LONGITUDE,
  UPDATE_SITE_NAME,
} from '@/store/modules/models/mutation-types';
import { getDiff } from '@/utils/Diff';
import formatNumber from '@/utils/NumberFormatter';
import Validatable, { IEntityChange } from '@/validator/Validatable';
import i18n from '@/vueI18n';

interface IUnitCountAPI {
  active: number;
  inactive: number;
}

interface ISiteMarketAPI {
  market_id: number;
}

export interface ISiteAPI {
  site_id: number;
  name: string;
  description: string;
  uuid: string;
  owner_id: number;
  active: boolean;
  external_id: string;
  latitude: number | string;
  longitude: number | string;
  location_id: number;
  location?: ILocationAPI;
  unit_count?: IUnitCountAPI;
  physical_unit_count?: number;
  units?: IUnitAPI[];
  created_on_utc?: string;
  modified_on_utc?: string;
  location_id_overridden?: boolean;
  site_markets?: ISiteMarketAPI[];
}

const type = 'Site';

export default class Site extends Validatable {
  apiObject: ISiteAPI;
  cachedOriginalObject: Site;

  type: string;
  name: string;
  description: string;
  uuid: string;
  ownerID?: number;
  isActive: boolean;
  externalID: string;
  latitude: number | string;
  longitude: number | string;
  locationID: number;
  location?: Location;
  unitCount?: IUnitCountAPI;
  physicalUnitCount: number;
  isScreenCountOverridden: boolean;
  units: Unit[];
  isStreetViewImageAvailable?: boolean;
  createdOnUtc?: DateTime;
  modifiedOnUtc?: DateTime;
  isLocationIDLocked: boolean;
  timestamp: DateTime;
  isInlineEdited: boolean;
  budgetBreakdownName: string;
  markets?: ISiteMarketAPI[];

  static entityPropertiesCached: Record<string, EntityProperty<Site>>;

  constructor(
    id = -1,
    name = '',
    description = '',
    uuid: string = null,
    ownerID: number = orgAppStore?.state?.organization?.id,
    isActive = true,
    externalID: string = null,
    latitude: number | string = null,
    longitude: number | string = null,
    locationID: number = null,
    location?: Location,
    unitCount?: IUnitCountAPI,
    physicalUnitCount = 0,
    units: Unit[] = [],
    createdOnUtc?: DateTime,
    modifiedOnUtc?: DateTime,
    isLocationIDLocked = false,
    markets?: ISiteMarketAPI[],
    budgetBreakdownName?: string,
  ) {
    super();
    this.type = type;
    this.id = id;
    this.name = name;
    this.description = description;
    this.uuid = uuid;
    this.ownerID = ownerID;
    this.isActive = isActive;
    this.externalID = externalID;
    this.latitude = latitude;
    this.longitude = longitude;
    this.locationID = locationID;
    this.location = location;
    this.unitCount = unitCount;
    this.physicalUnitCount = physicalUnitCount;
    this.isScreenCountOverridden = physicalUnitCount > (unitCount?.active || 0);
    this.units = units || [];
    this.isStreetViewImageAvailable = null;
    this.createdOnUtc = createdOnUtc;
    this.modifiedOnUtc = modifiedOnUtc;
    this.isLocationIDLocked = isLocationIDLocked;
    this.timestamp = DateTime.local();
    this.isInlineEdited = false;
    this.budgetBreakdownName = budgetBreakdownName;
    this.markets = markets;
  }

  // Other methods remain unchanged
  toApi(): ISiteAPI {
    const apiObject = {
      site_id: this.id >= 0 ? this.id : undefined,
      name: this.name,
      description: this.description,
      uuid: this.uuid != null ? this.uuid : undefined,
      owner_id: this.ownerID,
      active: this.isActive,
      external_id: this.externalID,
      latitude: isString(this.latitude) ? Number(this.latitude) : this.latitude,
      longitude: isString(this.longitude) ? Number(this.longitude) : this.longitude,
      location_id: this.locationID,
    };

    if (this.isInlineEdited) {
      Site.expandedProperties.forEach((property) => delete apiObject[property]);
    }

    return apiObject;
  }

  static {
    // When the user changes the language, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.languageChanged, () => {
      Site.entityPropertiesCached = null;
    });

    // When the user logs out or changes the organization, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.logout, () => {
      Site.entityPropertiesCached = null;
    });
    GlobalEventBus.$on(GlobalEvents.organizationChanged, () => {
      Site.entityPropertiesCached = null;
    });
  }

  get lat(): string | number {
    return this.latitude;
  }

  set lat(val: string | number) {
    this.latitude = val;
  }

  get lng(): string | number {
    return this.longitude;
  }

  set lng(val: string | number) {
    this.longitude = val;
  }

  get endpoint(): string {
    return `/sites/${this.id}`;
  }

  get displayName(): string {
    const orgSettings = getStore()?.state?.orgApp.organizationSettings;
    return orgSettings !== null && orgSettings.uiEnableDisplayOfSiteIDNextToSiteName
      ? `[${this.externalID}] - ${this.name}`
      : this.name;
  }

  get streetViewImageUrl(): string {
    return `https://maps.googleapis.com/maps/api/streetview?source=outdoor&size=536x402&location=${this.latitude},${this.longitude}&key=${GoogleMapsApiKey}`;
  }

  get isStreetViewImageAvailableUrl(): string {
    return `https://maps.googleapis.com/maps/api/streetview/metadata?source=outdoor&size=536x402&location=${this.latitude},${this.longitude}&key=${GoogleMapsApiKey}`;
  }

  get isActiveDescription(): string {
    if (this.isActive) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get totalIUnitCount(): number {
    if (this.isScreenCountOverridden) {
      return this.physicalUnitCount;
    }
    if (this.unitCount !== undefined && this.unitCount !== null) {
      return this.unitCount.active + this.unitCount.inactive;
    }
    return null;
  }

  get totalUnitCountFormatted(): string {
    return this.totalIUnitCount === null ? DASH : formatNumber(this.totalIUnitCount, '0,0');
  }

  get inactiveUnitCount(): number {
    if (this.unitCount !== undefined && this.unitCount !== null) {
      return this.unitCount.inactive;
    }
    return null;
  }

  get inactiveUnitCountFormatted(): string {
    return this.inactiveUnitCount === null ? DASH : formatNumber(this.inactiveUnitCount, '0,0');
  }

  get activeUnitCount(): number {
    if (this.unitCount !== undefined && this.unitCount !== null) {
      return this.unitCount.active;
    }
    return null;
  }

  get activeUnitCountFormatted(): string {
    return this.activeUnitCount === null ? DASH : formatNumber(this.activeUnitCount, '0,0');
  }

  get unitCountFormatted(): string {
    if (this.unitCount !== undefined && this.unitCount !== null) {
      return this.unitCount.inactive > 0
        ? `${this.totalUnitCountFormatted} <span class="badge badge-danger">${formatNumber(this.unitCount.inactive, '0,0')}</span>`
        : this.totalUnitCountFormatted;
    }
    return DASH;
  }

  async checkStreetViewImageAvailability(): Promise<void> {
    if (this.isStreetViewImageAvailable != null) {
      return;
    }
    // axios.get(this.isStreetViewImageAvailableUrl).then((streetViewResult) => {
    //   rootStore.dispatch('updateSiteIsStreetViewImageAvailable', { site: this, value: streetViewResult.data.status === 'OK' });
    // });

    const streetViewResult = await axios.get(this.isStreetViewImageAvailableUrl);
    await rootStore.dispatch('updateSiteIsStreetViewImageAvailable', {
      site: this,
      value: streetViewResult.data.status === 'OK',
    });
  }

  get createdOnFormatted(): string {
    return this.createdOnUtc != null ? this.createdOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get modifiedOn(): DateTime {
    return this.modifiedOnUtc != null ? this.modifiedOnUtc.toLocal() : null;
  }

  get modifiedOnFormatted(): string {
    return this.modifiedOn != null ? this.modifiedOn.toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get idAndName(): string {
    return `${this.externalID} - ${this.name}`;
  }

  get originalObject(): Site {
    if (this.apiObject == null) return null;
    if (this.cachedOriginalObject == null) {
      this.cachedOriginalObject = Site.fromApi(this.apiObject);
    }
    return this.cachedOriginalObject;
  }

  async getChanges(): Promise<IEntityChange<Site>[]> {
    if (this.isNew) return [];

    return super.getValidatableChanges<Site>(this.originalObject, Site.entityProperties);
  }

  isPristine(): boolean {
    if (this.isNew) return true;
    const ignoreProperties = [
      'archived',
      'created_on_utc',
      'geohash',
      'location',
      'location_id_overridden',
      'modified_on_utc',
      'physical_unit_count',
      'point',
      'unit_count',
      'units',
    ];
    const diffs = getDiff(this.apiObject, this.toApi(), ignoreProperties);
    if (diffs.length === 0) return true;
    DatadogApi.logUnexpectedChange(diffs);
    return false;
  }

  static fromApi(apiObject: ISiteAPI): Site {
    const site = new Site(
      apiObject.site_id,
      apiObject.name,
      apiObject.description,
      apiObject.uuid,
      apiObject.owner_id,
      apiObject.active,
      apiObject.external_id,
      apiObject.latitude,
      apiObject.longitude,
      apiObject.location_id,
      apiObject.location != null ? Location.fromApi(apiObject.location) : null,
      apiObject.unit_count,
      apiObject.physical_unit_count,
      apiObject.units != null ? apiObject.units.map((x) => Unit.fromApi(x)) : null,
      apiObject.created_on_utc != null ? DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }) : null,
      apiObject.modified_on_utc != null ? DateTime.fromISO(apiObject.modified_on_utc, { zone: 'utc' }) : null,
      apiObject.location_id_overridden != null ? apiObject.location_id_overridden : true,
      apiObject.site_markets,
    );
    site.apiObject = apiObject;
    return site;
  }

  // Other static methods remain unchanged
  static fromObject(obj): Site {
    return super.fromObject(obj, new Site());
  }

  static validatorCreateRules(): Record<string, string> {
    return {
      name: 'required|max:191|not_empty_or_whitespace',
      externalID: 'required|max:75|not_empty_or_whitespace',
      description: 'required|max:1000',
      latitude: 'required|numeric:float|min_value:-90|max_value:90',
      longitude: 'required|numeric:float|min_value:-180|max_value:180',
    };
  }

  static get expands(): string[] {
    return [
      'unit_count',
      'physical_unit_count',
      'units.unit_languages',
      'units.media_type',
      'units.unit_iab_blacklist',
      'units.unit_advertiser_blacklist',
      'location',
    ];
    //
    // UPDATE EXPANDEDPROPERTIES IF YOU ADD NEW EXPAND QUERY PARAMETER
    //
  }

  static get expandedProperties(): string[] {
    return [];
  }

  static get bulkEditProperties(): EntityProperty<Site>[] {
    return [
      this.entityProperties.isActive,
      this.entityProperties.description,
      this.entityProperties.latitude,
      this.entityProperties.longitude,
      this.entityProperties.externalID,
    ];
  }

  static get bulkV4Properties(): EntityProperty<Site>[] {
    return [
      this.entityProperties.externalID,
      this.entityProperties.name,
      this.entityProperties.description,
      this.entityProperties.latitude,
      this.entityProperties.longitude,
    ];
  }

  static get entityProperties(): Record<string, EntityProperty<Site>> {
    if (Site.entityPropertiesCached != null) {
      return Site.entityPropertiesCached;
    }

    const action = (site) => rootStore.dispatch('orgApp/saveSite', { site, showLoader: false });
    Site.entityPropertiesCached = {
      id: new EntityProperty({
        label: capitalize(i18n.global.t('names.siteID', { n: 1 })),
        type: TableColumnTypes.string,
        property: 'site_id',
        editablePropertyName: 'id',
      }),
      name: new EntityProperty({
        label: capitalize(i18n.global.t('names.name', { n: 1 })),
        property: 'name',
        type: TableColumnTypes.string,
        editablePropertyName: 'name',
        mutation: UPDATE_SITE_NAME,
        editableClassName: type,
        action,
      }),
      description: new EntityProperty({
        label: capitalize(i18n.global.t('names.internalNote', { n: 2 })),
        property: 'description',
        type: TableColumnTypes.string,
        editablePropertyName: 'description',
        mutation: UPDATE_SITE_DESCRIPTION,
        editableClassName: type,
        action,
      }),
      externalID: new EntityProperty({
        label: capitalize(i18n.global.t('names.siteID', { n: 1 })),
        property: 'external_id',
        type: TableColumnTypes.string,
        editablePropertyName: 'externalID',
        mutation: UPDATE_SITE_EXTERNALID,
        editableClassName: type,
        action,
      }),
      latitude: new EntityProperty({
        label: capitalize(i18n.global.t('names.latitude', { n: 1 })),
        property: 'latitude',
        type: TableColumnTypes.float,
        editablePropertyName: 'latitude',
        mutation: UPDATE_SITE_LATITUDE,
        editableClassName: type,
        action,
        canAffectAdDeliveryWhenModified: true,
      }),
      longitude: new EntityProperty({
        label: capitalize(i18n.global.t('names.longitude', { n: 1 })),
        property: 'longitude',
        type: TableColumnTypes.float,
        editablePropertyName: 'longitude',
        mutation: UPDATE_SITE_LONGITUDE,
        editableClassName: type,
        action,
        canAffectAdDeliveryWhenModified: true,
      }),
      isActive: new EntityProperty({
        label: `${capitalize(i18n.global.t('names.active'))}?`,
        type: TableColumnTypes.boolean,
        canAffectAdDeliveryWhenModified: true,
      }),
      unitCount: new EntityProperty({
        label: capitalize(i18n.global.t('names.screen', { n: 2 })),
        type: TableColumnTypes.integer,
      }),
      modifiedOn: new EntityProperty({
        label: capitalize(i18n.global.t('messages.lastModifiedOn')),
        property: 'modified_on_utc',
        type: TableColumnTypes.datetime,
      }),
      locationID: new EntityProperty({
        label: capitalize(i18n.global.t('names.location', { n: 1 })),
        type: TableColumnTypes.string,
        property: 'location_id',
        editablePropertyName: 'locationID',
        toStringOverride: (site: Site) => {
          return site.location != null ? site.location.name : site.locationID;
        },
      }),
    };

    return Site.entityPropertiesCached;
  }

  static get entityName(): string {
    return type;
  }

  static async getEntityIDsFromModels(models: Site[]): Promise<{ model: Site; id: number }[]> {
    if (models.length === 0) return [];

    try {
      const externalIDs = models.map((x) => x.externalID);
      const getSitesAction = ({ queryParams, cancelTokenSource, showLoader }) =>
        rootStore.dispatch('orgApp/getSites', { queryParams, cancelTokenSource, showLoader });
      const qp = QueryParams.all(FilterCollection.for('external_id', externalIDs, FilterComparisonOperator.IN));
      const response = (await qp.run(getSitesAction)) as { items: Site[] };
      const entities = response.items;
      return models.map((model) => ({ model, id: entities.find((x) => x.externalID === model.externalID)?.id }));
    } catch (e) {
      throw new Exception('Failed to get entity IDs.');
    }
  }

  static async assignIDs(entities: Site[]): Promise<Site[]> {
    if (entities.length === 0) return [];

    const existingIDMapping = await Site.getEntityIDsFromModels(entities);
    entities.forEach((x) => {
      x.id = existingIDMapping.find((y) => y.model === x)?.id || -1;
    });
    return entities;
  }

  static async patchEntities(entities: Site[]) {
    try {
      const payload = this.getPatchPayload(entities);
      await rootStore.dispatch('orgApp/patchSites', payload);
    } catch (e) {
      throw new Exception('Failed to patch sites.');
    }
  }

  static async getEntities(ids): Promise<Site[]> {
    if (ids.length === 0) return [];

    try {
      const queryParams = QueryParams.all(FilterCollection.for('site_id', ids, FilterComparisonOperator.IN));
      queryParams.expand = Site.expands;
      queryParams.showLoader = false;
      const response = await rootStore.dispatch('orgApp/getSites', {
        queryParams,
        cancelTokenSource: queryParams.cancelTokenSource,
        showLoader: false,
      });
      return response.items;
    } catch (e) {
      throw new Exception('Failed to get sites.');
    }
  }
}
